import React from 'react';
import { T } from '../lib/tokens';
import { Wordmark } from './ui/Wordmark';
import { logout } from '../lib/auth';
import { useIsMobile } from '../lib/useIsMobile';
import { BottomTabBar, type TabId } from './mobile/BottomTabBar';
import { MobileHeader } from './mobile/MobileHeader';

interface Props {
  page: string;
  userEmail: string;
  reviewCount?: number;
  children: React.ReactNode;
}

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Receipts', matches: ['dashboard', 'review', 'detail'] },
  { id: 'upload', label: 'Upload', matches: ['upload'] },
  { id: 'rules', label: 'Rules', matches: ['rules'] },
  { id: 'settings', label: 'Settings', matches: ['settings'] },
];

function pageToTab(page: string): TabId {
  if (page === 'upload') return 'upload';
  if (page === 'rules') return 'rules';
  if (page === 'settings') return 'settings';
  return 'receipts';
}

function handleTabChange(tab: TabId) {
  if (tab === 'receipts') window.location.hash = '#/';
  else window.location.hash = `#/${tab}`;
}

export function AppShell({ page, userEmail, reviewCount = 0, children }: Props) {
  const isMobile = useIsMobile();

  if (isMobile) {
    const isDetailPage = page === 'detail';
    return (
      <div style={{ height: '100dvh', background: T.paper, color: T.graphite, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!isDetailPage && (
          <MobileHeader
            showWordmark={true}
            onBack={page === 'review' ? () => { window.location.hash = '#/'; } : undefined}
          />
        )}
        <main style={{ flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch', padding: isDetailPage ? 0 : '0 20px 20px' }}>
          {children}
        </main>
        {!isDetailPage && (
          <BottomTabBar
            active={pageToTab(page)}
            onChange={handleTabChange}
            reviewCount={reviewCount}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: T.paper, color: T.graphite }}>
      <header
        style={{
          padding: '18px 32px',
          borderBottom: '1px solid #F0ECE3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#fff',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
          <a href="#/" style={{ textDecoration: 'none', cursor: 'pointer' }}>
            <Wordmark size={20} color={T.graphite} />
          </a>
          <nav style={{ display: 'flex', gap: 28, fontSize: 14 }}>
            {NAV_ITEMS.map((item) => {
              const active = item.matches.includes(page);
              return (
                <a
                  key={item.id}
                  href={`#/${item.id === 'dashboard' ? '' : item.id}`}
                  style={{
                    color: active ? T.graphite : T.graphiteDim,
                    fontWeight: active ? 500 : 400,
                    textDecoration: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {item.label}
                </a>
              );
            })}
          </nav>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 13, color: T.graphiteDim }}>{userEmail}</span>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: T.sage,
              color: T.navy,
              fontSize: 12,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {userEmail.charAt(0).toUpperCase()}
          </div>
          <button
            onClick={async () => {
              await logout();
              window.location.href = 'https://receiptrec.com';
            }}
            style={{
              background: 'transparent',
              border: 0,
              color: T.graphiteDim,
              fontSize: 12,
              cursor: 'pointer',
              fontFamily: "'Geist', sans-serif",
            }}
          >
            Sign out
          </button>
        </div>
      </header>
      <main style={{ maxWidth: 1240, margin: '0 auto', padding: '32px 40px 80px' }}>
        {children}
      </main>
    </div>
  );
}
